import { FC, useState, useMemo } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Radio,
  RadioGroup,
  Autocomplete
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { deepEqual } from 'fast-equals';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  getOptionPhases,
  getOptionSets,
  getCostTypes,
  getPriceTypes,
  getTaskCutoff,
  getColorPalettes,
  getUnitsOfMeasure,
  getCostingRules,
  getOptionTerritories,
  getNotificationTasks,
  getAffectedTasks,
  getOptionLockouts,
  getStandardFeaturelookup,
  createOption,
  updateOptionInfo,
  updateOptionImage
} from '../../../../fetch';
import { IDropdownResponse, IOptionInfo, IOptionDropdown } from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';
import { OptionImages } from './option-images';

interface IGeneralInformation {
  generalInfo: IOptionInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  amount: Yup.number(),
  colorPaletteId: Yup.string(),
  costTypeId: Yup.number().required('Required'),
  cutoffTaskPartClassId: Yup.number().required('Required'),
  excludedTerritories: Yup.array().of(Yup.number()),
  imageUrl: Yup.string(),
  name: Yup.string().required('Required'),
  optionSetId: Yup.number().required('Required'),
  phaseId: Yup.number().required('Required'),
  priceTypeId: Yup.number().required('Required'),
  unitOfMeasureId: Yup.number().required('Required'),
  visibilityRuleId: Yup.string().required('Required')
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { optionId }: { optionId: string } = useParams();
  const [selectedTerritoryValues, setSelectedTerritoryValues] = useState<IDropdownResponse[]>([]);
  const [selectedNotificationTaskValues, setSelectedNotificationTaskValues] = useState<
    IOptionDropdown[]
  >([]);
  const [selectedAffectedTaskValues, setSelectedAffectedTaskValues] = useState<IOptionDropdown[]>(
    []
  );
  const [selectedLockedOptionValues, setSelectedLockedOptionValues] = useState<IDropdownResponse[]>(
    []
  );
  const [selectedLockedStandardFeaturesValues, setSelectedLockedStandardFeaturesValues] = useState<
    IDropdownResponse[]
  >([]);
  const [inputSelectedTerritory, setInputSelectedTerritory] = useState('');
  const [inputSelectedNotificationTask, setInputSelectedNotificationTask] = useState('');
  const [inputSelectedAffectedTask, setInputSelectedAffectedTask] = useState('');
  const [inputSelectedLockedOption, setInputSelectedLockedOption] = useState('');
  const [inputSelectedLockedStandardFeature, setInputSelectedLockedStandardFeature] = useState('');
  const [imageFormData, setImageFormData] = useState<any>();

  const initialNotificationTasks = generalInfo?.notificationTasks.map(obj => obj.value) || [];
  const initialAffectedTasks = generalInfo?.affectedTasks.map(obj => obj.value) || [];

  const { isLoading: isLoadingOptionPhases, data: optionPhases } = useQuery<
    IDropdownResponse[],
    Error
  >(['getOptionPhases'], () => getOptionPhases());

  const { isLoading: isLoadingOptionSets, data: optionSets } = useQuery<IDropdownResponse[], Error>(
    ['getOptionSets'],
    () => getOptionSets()
  );

  const { isLoading: isLoadingCostTypes, data: costTypes } = useQuery<IDropdownResponse[], Error>(
    ['getCostTypes'],
    () => getCostTypes()
  );

  const { isLoading: isLoadingPriceTypes, data: priceTypes } = useQuery<IDropdownResponse[], Error>(
    ['getPriceTypes'],
    () => getPriceTypes()
  );

  const { isLoading: isLoadingTaskCutoff, data: taskCutoff } = useQuery<IDropdownResponse[], Error>(
    ['getTaskCutoff'],
    () => getTaskCutoff()
  );

  const { isLoading: isLoadingColorPalettes, data: colorPalettes } = useQuery<
    IDropdownResponse[],
    Error
  >(['getColorPalettes'], () => getColorPalettes());

  const { isLoading: isLoadingUnitsOfMeasure, data: unitsOfMeasure } = useQuery<
    IDropdownResponse[],
    Error
  >(['getUnitsOfMeasure'], () => getUnitsOfMeasure());

  const { isLoading: isLoadingCostingRules, data: costingRules } = useQuery<
    IDropdownResponse[],
    Error
  >(['getCostingRules'], () => getCostingRules());

  const { isLoading: isLoadingOptionTerritories, data: optionTerritories } = useQuery<
    IDropdownResponse[],
    Error
  >(['getOptionTerritories'], () => getOptionTerritories(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return generalInfo?.excludedTerritories?.includes(Number(record.value));
      });
      setSelectedTerritoryValues(options);
    },
    enabled: generalInfo !== undefined || optionId === 'add'
  });
  const optionTerritoryData = useMemo(() => optionTerritories ?? [], [optionTerritories]);

  const { isLoading: isLoadingNotificationTasks, data: notificationTasks } = useQuery<
    IOptionDropdown[],
    Error
  >(['getNotificationTasks'], () => getNotificationTasks(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return initialNotificationTasks?.includes(record.value);
      });
      setSelectedNotificationTaskValues(options);
    },
    enabled: generalInfo !== undefined || optionId === 'add'
  });
  const notificationTaskData = useMemo(() => notificationTasks ?? [], [notificationTasks]);

  const { isLoading: isLoadingAffectedTasks, data: affectedTasks } = useQuery<
    IOptionDropdown[],
    Error
  >(['getAffectedTasks'], () => getAffectedTasks(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return initialAffectedTasks.includes(record.value);
      });
      setSelectedAffectedTaskValues(options);
    },
    enabled: generalInfo !== undefined || optionId === 'add'
  });
  const affectedTaskData = useMemo(() => affectedTasks ?? [], [affectedTasks]);

  const { isLoading: isLoadingLockedOptions, data: lockedOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getOptionLockouts'], () => getOptionLockouts(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return generalInfo?.lockedOptions?.includes(Number(record.value));
      });
      setSelectedLockedOptionValues(options);
    },
    enabled: generalInfo !== undefined || optionId === 'add'
  });
  const lockedOptionData = useMemo(() => lockedOptions ?? [], [lockedOptions]);

  const { isLoading: isLoadingLockedStandardFeatures, data: lockedStandardFeatures } = useQuery<
    IDropdownResponse[],
    Error
  >(['getStandardFeaturelookup'], () => getStandardFeaturelookup(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return generalInfo?.lockedStandardFeatures?.includes(Number(record.value));
      });
      setSelectedLockedStandardFeaturesValues(options);
    },
    enabled: generalInfo !== undefined || optionId === 'add'
  });
  const lockedStandardFeatureData = useMemo(
    () => lockedStandardFeatures ?? [],
    [lockedStandardFeatures]
  );

  return (
    <>
      {isLoadingGeneralInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            amount: generalInfo?.amount ?? '',
            amountIsPercent:
              generalInfo?.amountIsPercent === true
                ? 'Yes'
                : generalInfo?.amountIsPercent === false
                ? 'No'
                : '',
            colorPaletteId: generalInfo?.colorPaletteId ?? '',
            costTypeId: generalInfo?.costTypeId ?? '1',
            cutoffTaskPartClassId: generalInfo?.cutoffTaskPartClassId ?? '0',
            excludedTerritories: generalInfo?.excludedTerritories ?? [],
            notificationTasks: initialNotificationTasks,
            affectedTasks: initialAffectedTasks,
            lockedOptions: generalInfo?.lockedOptions ?? [],
            lockedStandardFeatures: generalInfo?.lockedStandardFeatures ?? [],
            name: generalInfo?.name ?? '',
            optionSetId: generalInfo?.optionSetId ?? '0',
            phaseId: generalInfo?.phaseId ?? '775',
            priceTypeId: generalInfo?.priceTypeId ?? '1',
            unitOfMeasureId: generalInfo?.unitOfMeasureId ?? '15',
            visibilityRuleId: generalInfo?.visibilityRuleId ?? 'Universal',
            imageUrl: generalInfo?.imageUrl ?? ''
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IOptionInfo = {
                ...values,
                amountIsPercent: values.amountIsPercent === 'Yes' ? true : false,
                amount: Number(values.amount) ?? null,
                phaseId: Number(values.phaseId) ?? null,
                optionSetId: Number(values.optionSetId) ?? null,
                costTypeId: Number(values.costTypeId) ?? 1,
                priceTypeId: Number(values.priceTypeId) ?? 1,
                cutoffTaskPartClassId: Number(values.cutoffTaskPartClassId) ?? null,
                colorPaletteId: values.colorPaletteId ? Number(values.colorPaletteId) : null,
                unitOfMeasureId: Number(values.unitOfMeasureId) ?? 15,
                visibilityRuleId: values.visibilityRuleId ?? 'Universal',
                notificationTasks:
                  typeof values.notificationTasks[0] === 'object'
                    ? values.notificationTasks
                    : generalInfo?.notificationTasks,
                affectedTasks:
                  typeof values.affectedTasks[0] === 'object'
                    ? values.affectedTasks
                    : generalInfo?.affectedTasks
              };

              if (optionId !== 'add') {
                await updateOptionInfo(optionId, data);
                refetchGeneralInfo();
                enqueueSnackbar('Option has been updated successfully!', {
                  variant: 'success'
                });
              }
              if (optionId === 'add') {
                const option = await createOption(data);
                if (imageFormData) {
                  await updateOptionImage(option.data, imageFormData);
                }
                actions.resetForm();

                enqueueSnackbar('Option has been created successfully!', {
                  variant: 'success'
                });

                history.push(`/development/options/${option.data}/general-information`);
              }
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Option was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                <Form onSubmit={handleSubmit}>
                  {isSubmitting && (
                    <Loader type="overlay" position="centered">
                      Saving...
                    </Loader>
                  )}
                  <Prompt
                    when={!deepEqual(initialValues, values)}
                    message="You have unsaved changes, are you sure you want to exit?"
                  />
                  <Box mt={2}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Name"
                          name="name"
                          size="small"
                          value={values.name}
                          onBlur={handleBlur}
                          onChange={e => setFieldValue('name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SMBSelect
                          required
                          onChange={e => {
                            setFieldValue('phaseId', e.target.value);
                          }}
                          variant="outlined"
                          handleClear={() => setFieldValue('phaseId', '')}
                          name={`phaseId`}
                          label={`Phase`}
                          id={`phaseId`}
                          value={values.phaseId}
                          onBlur={handleBlur}
                          disabled={isLoadingOptionPhases}
                          error={errors && errors.phaseId ? true : false}
                        >
                          {optionPhases &&
                            optionPhases.map((phase: any, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={phase.value}>
                                  {phase.description}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              onChange={e => {
                                setFieldValue('optionSetId', e.target.value);
                              }}
                              variant="outlined"
                              handleClear={() => setFieldValue('phase', '')}
                              name={`optionSetId`}
                              label={`Option Set`}
                              id={`optionSetId`}
                              value={values.optionSetId}
                              onBlur={handleBlur}
                              disabled={isLoadingOptionSets}
                              error={errors && errors.optionSetId ? true : false}
                            >
                              {optionSets &&
                                optionSets.map((optionSet: any, index) => {
                                  return (
                                    <MenuItem key={`${index + 1}`} value={optionSet.value}>
                                      {optionSet.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset" required>
                          <FormLabel>Cost Type</FormLabel>
                          <RadioGroup
                            aria-label="costTypeId"
                            name="costTypeId"
                            value={values.costTypeId}
                            onChange={e => setFieldValue('costTypeId', e.target.value)}
                          >
                            {costTypes?.length > 0 &&
                              costTypes.map(costType => {
                                return (
                                  <FormControlLabel
                                    aria-required
                                    disabled={isLoadingCostTypes}
                                    key={costType.value}
                                    value={costType.value}
                                    control={<Radio />}
                                    label={costType.description}
                                  />
                                );
                              })}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset" required>
                          <FormLabel>Customer Pricing Method</FormLabel>
                          <RadioGroup
                            aria-required
                            aria-label="priceTypeId"
                            name="priceTypeId"
                            value={values.priceTypeId}
                            onChange={e => setFieldValue('priceTypeId', e.target.value)}
                          >
                            {priceTypes?.length > 0 &&
                              priceTypes.map(priceType => {
                                return (
                                  <FormControlLabel
                                    disabled={
                                      isLoadingPriceTypes || Number(values.costTypeId) === 4
                                    }
                                    key={priceType.value}
                                    value={priceType.value}
                                    control={<Radio />}
                                    label={priceType.description}
                                  />
                                );
                              })}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {String(values?.priceTypeId) === '2' && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            autoComplete="nope"
                            label="Amount"
                            name="amount"
                            size="small"
                            value={values.amount}
                            onBlur={handleBlur}
                            onChange={e => setFieldValue('amount', e.target.value)}
                          />
                        </Grid>
                      )}
                      {String(values?.priceTypeId) === '3' && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Amount"
                              name="amount"
                              size="small"
                              value={values.amount}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('amount', e.target.value)}
                            />
                          </Grid>
                          <Grid container spacing={1} xs={12} sm={6} alignItems="center">
                            <Grid item ml={1}>
                              <FormLabel component="legend">Is Percent</FormLabel>
                            </Grid>
                            <Grid item>
                              <RadioGroup
                                row
                                aria-label="amountIsPercent"
                                name="amountIsPercent"
                                value={values?.amountIsPercent}
                                onChange={e => setFieldValue('amountIsPercent', e.target.value)}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </Grid>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} sm={6}>
                        <SMBSelect
                          required
                          onChange={e => {
                            setFieldValue('cutoffTaskPartClassId', e.target.value);
                          }}
                          variant="outlined"
                          handleClear={() => setFieldValue('cutoffTaskPartClassId', '')}
                          name={`cutoffTaskPartClassId`}
                          label={`Task Cutoff`}
                          id={`cutoffTaskPartClassId`}
                          value={values.cutoffTaskPartClassId}
                          onBlur={handleBlur}
                          disabled={isLoadingTaskCutoff}
                          error={errors && errors.cutoffTaskPartClassId ? true : false}
                        >
                          {taskCutoff &&
                            taskCutoff.map((task: any, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={task.value}>
                                  {task.description}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SMBSelect
                          onChange={e => {
                            setFieldValue('colorPaletteId', e.target.value);
                          }}
                          variant="outlined"
                          handleClear={() => setFieldValue('colorPaletteId', '')}
                          name={`colorPaletteId`}
                          label={`Color Palette`}
                          id={`colorPaletteId`}
                          value={values.colorPaletteId}
                          onBlur={handleBlur}
                          disabled={isLoadingColorPalettes}
                          error={errors && errors.colorPaletteId ? true : false}
                        >
                          {colorPalettes &&
                            colorPalettes.map((color: any, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={color.value}>
                                  {color.description}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SMBSelect
                          required
                          onChange={e => {
                            setFieldValue('unitOfMeasureId', e.target.value);
                          }}
                          variant="outlined"
                          handleClear={() => setFieldValue('unitOfMeasureId', '')}
                          name={`unitOfMeasureId`}
                          label={`Unit of Measure`}
                          id={`unitOfMeasureId`}
                          value={values.unitOfMeasureId}
                          onBlur={handleBlur}
                          disabled={isLoadingUnitsOfMeasure}
                          error={errors && errors.unitOfMeasureId ? true : false}
                        >
                          {unitsOfMeasure &&
                            unitsOfMeasure.map((unit: any, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={unit.value}>
                                  {unit.description}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SMBSelect
                          required
                          onChange={e => {
                            setFieldValue('visibilityRuleId', e.target.value);
                          }}
                          variant="outlined"
                          handleClear={() => setFieldValue('visibilityRuleId', '')}
                          name={`visibilityRuleId`}
                          label={`Costing Rule`}
                          id={`visibilityRuleId`}
                          value={values.visibilityRuleId}
                          onBlur={handleBlur}
                          disabled={isLoadingCostingRules || Number(values.costTypeId) === 4}
                          error={errors && errors.visibilityRuleId ? true : false}
                        >
                          {costingRules &&
                            costingRules.map((rule: any, index) => {
                              return (
                                <MenuItem key={`${index + 1}`} value={rule.value}>
                                  {rule.description}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          id="excludedTerritories"
                          value={selectedTerritoryValues}
                          onChange={(event, newValue: any[]) => {
                            setSelectedTerritoryValues(newValue);
                            setFieldValue(
                              'excludedTerritories',
                              newValue.map(val => val.value)
                            );
                          }}
                          inputValue={inputSelectedTerritory}
                          onInputChange={(event, newInputValue) => {
                            setInputSelectedTerritory(newInputValue);
                          }}
                          options={optionTerritoryData}
                          getOptionLabel={option => option.description}
                          filterSelectedOptions
                          renderOption={(props, option: any) => {
                            return (
                              <li {...props} key={`${option.value}`}>
                                {option.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingOptionTerritories}
                              label={
                                isLoadingOptionTerritories ? 'Loading...' : 'Excluded Territories'
                              }
                              placeholder="Territories"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          id="notificationTasks"
                          value={selectedNotificationTaskValues}
                          onChange={(event, newValue: any[]) => {
                            setSelectedNotificationTaskValues(newValue);
                            setFieldValue('notificationTasks', newValue);
                          }}
                          inputValue={inputSelectedNotificationTask}
                          onInputChange={(event, newInputValue) => {
                            setInputSelectedNotificationTask(newInputValue);
                          }}
                          options={notificationTaskData}
                          getOptionLabel={option => option.description}
                          filterSelectedOptions
                          renderOption={(props, option: any) => {
                            return (
                              <li {...props} key={`${option.value}`}>
                                {option.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingNotificationTasks}
                              label={
                                isLoadingNotificationTasks
                                  ? 'Loading...'
                                  : 'Non Critical Notifications'
                              }
                              placeholder="Notifications"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          id="affectedTasks"
                          value={selectedAffectedTaskValues}
                          onChange={(event, newValue: any[]) => {
                            setSelectedAffectedTaskValues(newValue);
                            setFieldValue('affectedTasks', newValue);
                          }}
                          inputValue={inputSelectedAffectedTask}
                          onInputChange={(event, newInputValue) => {
                            setInputSelectedAffectedTask(newInputValue);
                          }}
                          options={affectedTaskData}
                          getOptionLabel={option => option.description}
                          filterSelectedOptions
                          renderOption={(props, option: any) => {
                            return (
                              <li {...props} key={`${option.value}`}>
                                {option.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingAffectedTasks}
                              label={isLoadingAffectedTasks ? 'Loading...' : 'Affected Tasks'}
                              placeholder="Tasks"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          id="lockedOptions"
                          value={selectedLockedOptionValues}
                          onChange={(event, newValue: any[]) => {
                            setSelectedLockedOptionValues(newValue);
                            setFieldValue(
                              'lockedOptions',
                              newValue.map(val => val.value)
                            );
                          }}
                          inputValue={inputSelectedLockedOption}
                          onInputChange={(event, newInputValue) => {
                            setInputSelectedLockedOption(newInputValue);
                          }}
                          options={lockedOptionData}
                          getOptionLabel={option => option.description}
                          filterSelectedOptions
                          renderOption={(props, option: any) => {
                            return (
                              <li {...props} key={`${option.value}`}>
                                {option.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingLockedOptions}
                              label={isLoadingLockedOptions ? 'Loading...' : 'Option Lockouts'}
                              placeholder="Options"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          id="lockedStandardFeatures"
                          value={selectedLockedStandardFeaturesValues}
                          onChange={(event, newValue: any[]) => {
                            setSelectedLockedStandardFeaturesValues(newValue);
                            setFieldValue(
                              'lockedStandardFeatures',
                              newValue.map(val => val.value)
                            );
                          }}
                          inputValue={inputSelectedLockedStandardFeature}
                          onInputChange={(event, newInputValue) => {
                            setInputSelectedLockedStandardFeature(newInputValue);
                          }}
                          options={lockedStandardFeatureData}
                          getOptionLabel={option => option.description}
                          filterSelectedOptions
                          renderOption={(props, option: any) => {
                            return (
                              <li {...props} key={`${option.value}`}>
                                {option.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingLockedStandardFeatures}
                              label={
                                isLoadingLockedStandardFeatures
                                  ? 'Loading...'
                                  : 'Standard Feature Lockouts'
                              }
                              placeholder="Features"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <OptionImages
                              optionId={optionId}
                              imageTitle={'Image'}
                              image={values.imageUrl}
                              imageData={value => setImageFormData(value)}
                              refetchImage={refetchGeneralInfo}
                            ></OptionImages>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={optionId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/options');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
